import { supabase } from '@/integrations/supabase/client';

interface ClientError {
  category: 'frontend_js' | 'edge_function' | 'database_anomaly' | 'user_pattern';
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  message?: string;
  url?: string;
  stack?: string;
  user_agent?: string;
  status_code?: number;
  endpoint?: string;
  source?: string;
  page_context?: {
    route: string;
    caseId?: string;
    customerId?: string;
  };
  trace_id?: string;
}

// Batch errors to avoid flooding the backend
let errorQueue: ClientError[] = [];
let flushTimeout: ReturnType<typeof setTimeout> | null = null;
let isTrackingInitialized = false;
const MAX_QUEUE = 20;
const FLUSH_INTERVAL = 10_000; // 10 seconds
const LOG_FUNCTION_PATH = '/functions/v1/log-client-error';

function scheduleFlush() {
  if (flushTimeout) return;
  flushTimeout = setTimeout(flushErrors, FLUSH_INTERVAL);
}

async function flushErrors() {
  flushTimeout = null;
  if (errorQueue.length === 0) return;

  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();

    // No authenticated user -> drop queue to avoid endless 401 loop
    if (!session?.access_token) {
      errorQueue = [];
      return;
    }

    const batch = errorQueue.splice(0, MAX_QUEUE);
    const { error } = await supabase.functions.invoke('log-client-error', {
      body: { errors: batch },
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (error) throw error;
  } catch {
    // Silently fail — don't cause more errors from error tracking
  }
}

function getPageContext() {
  const pathname = window.location.pathname;
  return {
    route: pathname,
    caseId: pathname.match(/\/cases\/([a-f0-9-]+)/)?.[1] || undefined,
    customerId: pathname.match(/\/customers\/([a-f0-9-]+)/)?.[1] || undefined,
  };
}

export function trackError(error: ClientError) {
  // Skip errors from Lovable preview environment — not production issues
  if (typeof window !== 'undefined' && window.location.hostname.includes('lovableproject.com')) {
    return;
  }

  // Deduplicate by title within queue
  const exists = errorQueue.some(
    (e) => e.title === error.title && e.category === error.category
  );
  if (exists) return;

  errorQueue.push({
    ...error,
    url: error.url || window.location.href,
    user_agent: navigator.userAgent,
    page_context: error.page_context || getPageContext(),
    trace_id:
      error.trace_id ||
      (typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : undefined),
  });

  if (errorQueue.length >= MAX_QUEUE) {
    void flushErrors();
  } else {
    scheduleFlush();
  }
}

// Track unhandled JS errors
export function initErrorTracking() {
  if (isTrackingInitialized) return;
  isTrackingInitialized = true;

  window.addEventListener('error', (event) => {
    // Filter: ResizeObserver browser noise
    if (event.message?.includes('ResizeObserver loop')) return;

    // Filter: ChunkLoadError on new deploy — show version banner instead
    if (event.message?.includes('ChunkLoadError') || event.message?.includes('Loading chunk')) {
      window.dispatchEvent(new CustomEvent('app-new-version'));
      return;
    }

    trackError({
      category: 'frontend_js',
      severity: 'high',
      title: event.message || 'Ukendt JS-fejl',
      message: event.message,
      stack: event.error?.stack,
      source: `${event.filename}:${event.lineno}:${event.colno}`,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message = reason instanceof Error ? reason.message : String(reason);

    // Filter: AbortError from React Query cancellation
    if (message.includes('AbortError') || message.includes('signal is aborted')) return;

    // Filter: ChunkLoadError on new deploy
    if (message.includes('ChunkLoadError') || message.includes('Loading chunk')) {
      window.dispatchEvent(new CustomEvent('app-new-version'));
      return;
    }

    trackError({
      category: 'frontend_js',
      severity: 'high',
      title: `Unhandled rejection: ${message.slice(0, 100)}`,
      message,
      stack: reason instanceof Error ? reason.stack : undefined,
    });
  });

  // Intercept fetch to track API errors
  const originalFetch = window.fetch;
  window.fetch = async (...args) => {
    try {
      const response = await originalFetch(...args);
      const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request)?.url || '';
      const isLogClientError = url.includes(LOG_FUNCTION_PATH);

      // Track 4xx/5xx responses from our backend
      if (!response.ok && url.includes('supabase.co') && !isLogClientError) {
        const isAuthRefresh = url.includes('/auth/v1/token');
        const isAuthSettings = url.includes('/auth/v1/settings');
        const isAuthLogout = url.includes('/auth/v1/logout');
        const isAuthUser = url.includes('/auth/v1/user');
        // Don't track 404/406 on .maybeSingle() queries — expected for deleted entities
        const isExpectedNotFound = response.status === 406 || response.status === 404;
        // Don't track 403 on agent_statuses heartbeat — expected when session is stale
        const isHeartbeatExpected = (response.status === 403 || response.status === 401) && url.includes('agent_statuses');
        const isAiInsights401 = response.status === 401 && url.includes('ai-insights');
        // Don't track 409 PATCH conflicts on REST endpoints — typically optimistic-concurrency
        // races between client edits and DB triggers; not user-actionable.
        const method = (typeof args[1] === 'object' && args[1]?.method) || (args[0] instanceof Request ? args[0].method : 'GET');
        const isOptimisticConflict = response.status === 409 && url.includes('/rest/v1/') && (method === 'PATCH' || method === 'POST');
        if (!isAuthRefresh && !isAuthSettings && !isAuthLogout && !isAuthUser && !isExpectedNotFound && !isHeartbeatExpected && !isAiInsights401 && !isOptimisticConflict) {
          const endpoint = url.split('/').pop()?.split('?')[0] || 'unknown';
          const isEdgeFunction = url.includes('/functions/v1/');
          trackError({
            category: isEdgeFunction
              ? 'edge_function'
              : response.status === 401 || response.status === 403
                ? 'user_pattern'
                : 'database_anomaly',
            severity: response.status >= 500 ? 'high' : 'medium',
            title: `HTTP ${response.status}: ${endpoint}`,
            message: `${response.status} ${response.statusText}`,
            status_code: response.status,
            endpoint: url.replace(/https?:\/\/[^/]+/, ''),
          });
        }
      }

      return response;
    } catch (err) {
      // Network errors (connectivity issues)
      const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request)?.url || '';
      const isLogClientError = url.includes(LOG_FUNCTION_PATH);
      // Intentional cancellations (navigation, new message, idle-timeout abort)
      // are not real failures — never report them.
      const isAbort = (err as any)?.name === 'AbortError'
        || (err instanceof Error && /aborted|abort/i.test(err.message));
      if (isAbort) throw err;
      if (url.includes('supabase.co') && !isLogClientError) {
        const isAuthRefresh = url.includes('/auth/v1/token');
        const isEdgeFunction = url.includes('/functions/v1/');
        const endpoint = url.split('/').pop()?.split('?')[0] || 'unknown';
        // Filter network errors that are expected when device is offline / tab backgrounded:
        // - auth refresh (handled by Supabase auto-retry)
        // - agent_statuses heartbeat (presence ping, runs every 60s)
        // - storage uploads with extension in last segment (will be retried by user)
        const isPresencePing = url.includes('agent_statuses');
        if (isAuthRefresh || isPresencePing) {
          throw err;
        }
        trackError({
          category: isEdgeFunction ? 'edge_function' : 'database_anomaly',
          severity: isEdgeFunction ? 'high' : 'medium',
          title: `Netværksfejl: ${endpoint}`,
          message: err instanceof Error ? err.message : 'Network error',
          endpoint: url.replace(/https?:\/\/[^/]+/, ''),
        });
      }
      throw err;
    }
  };

  // Flush when tab goes to background
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && errorQueue.length > 0) {
      void flushErrors();
    }
  });
}
